import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { VideoPlayerHandle } from "@fronterahealth/frontera-ui-components";

import {
  SessionMediaReviewActivityLogEnum,
  useCreateUpdateSessionMediaReviewActivityLogMutation,
} from "@api/graphql/types-and-hooks";
import { CommentsTimeline } from "@pages/VideoSessions/components/EmojiComments/CommentsTimeline";
import { EmojiCommentButtons } from "@pages/VideoSessions/components/EmojiComments/EmojiCommentButtons";
import { MomentsTimeline } from "@pages/VideoSessions/components/EmojiComments/MomentsTimeline";
import { VideoPlayerContent } from "@pages/VideoSessions/components/VideoPlayerContent";
import { useEmojiMarkers } from "@pages/VideoSessions/hooks/useEmojiMarkers";
import { useAdminData } from "@providers/AdminDataProvider";
import { VideoSessionTab, useVideoSessionData } from "@providers/VideoSessionProvider";

const localStorageKey = "video_progress";

interface VideoContainerProps {
  videoUrl: string;
  onMarkerClick: (time: number, commentId: string) => void;
  onMomentMarkerClick: (startTime: number, endTime: number, momentId: string) => void;
  onTimelineClick: (time: number) => void;
  externalPlayerRef?: React.RefObject<VideoPlayerHandle | null>; // To handle player in the details view
  commentCount?: string;
  onDurationChange: (duration: number) => void;
  onSeeking?: () => void;
  onSeeked?: () => void;
  enabledFocus?: boolean;
  onPlayerReady?: () => void;
}

interface VideoProgressType {
  startTime: number;
  endTime: number;
  watchedSeconds: number;
  activityLogId?: string;
}
export const VideoContainer: React.FC<VideoContainerProps> = ({
  videoUrl,
  onMarkerClick,
  onMomentMarkerClick,
  externalPlayerRef,
  commentCount,
  onDurationChange,
  onTimelineClick,
  onSeeked,
  onSeeking,
  enabledFocus = true,
  onPlayerReady,
}) => {
  const localPlayerRef = useRef<VideoPlayerHandle>(null);
  const playerRef = externalPlayerRef || localPlayerRef;
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const currentTimeRef = useRef<number>(0);
  const playStartedTimeRef = useRef<number | null>(null);
  const watchedSecondsRef = useRef<number>(0);
  const mediaActivityLogIdRef = useRef<string | null>(null);
  const tickRef = useRef<number>(0);
  const callActivityLogAPIRef = useRef<() => Promise<void>>(async () => {});

  const {
    setSelectedTab,
    getMediaByIdQuery: { isFetching },
    selectedMedia,
  } = useVideoSessionData();

  const [currentDuration, setCurrentDuration] = useState(0);

  const [currentTime, setCurrentTime] = useState(0);

  const { isUserBCBA } = useAdminData();

  const mediaId = selectedMedia?.id || "";

  const saveSessionActivityMutation = useCreateUpdateSessionMediaReviewActivityLogMutation();
  const isSessionTracking = !!(isUserBCBA && selectedMedia && !selectedMedia.isViewed);

  const getCurrentTime = () => {
    return playerRef.current?.player?.currentTime() || 0;
  };

  const handleDurationChange = (duration: number) => {
    setCurrentDuration(duration);
    onDurationChange(duration);
  };

  const {
    handleEmojiClick,
    selectedEmoji,
    handleCommentSubmit,
    closeCommentBox,
    emojiMarkers,
    isLoadingComments,
    isCommentSaving,
  } = useEmojiMarkers({
    mediaId,
    videoDuration: currentDuration,
  });

  const handleMarkerClick = (timestamp: number, commentId: string) => {
    setSelectedTab(VideoSessionTab.Activity);
    onMarkerClick(timestamp, commentId);
  };

  const handleMomentMarkerClick = (startTime: number, endTime: number, momentId: string) => {
    setSelectedTab(VideoSessionTab.Moments);
    onMomentMarkerClick(startTime, endTime, momentId);
  };

  // --- LocalStorage Helpers ---
  const saveProgressToLocalStorage = () => {
    try {
      const raw = localStorage.getItem(localStorageKey);
      const allProgress: Record<string, VideoProgressType> = raw ? JSON.parse(raw) : {};
      allProgress[mediaId] = {
        startTime: playStartedTimeRef.current ?? 0,
        endTime: currentTimeRef.current,
        watchedSeconds: watchedSecondsRef.current,
        activityLogId: mediaActivityLogIdRef.current ?? undefined,
      };
      localStorage.setItem(localStorageKey, JSON.stringify(allProgress));
    } catch (e) {
      console.error("Error saving to localStorage", e);
    }
  };

  const getSavedProgress = (): VideoProgressType => {
    try {
      const raw = localStorage.getItem(localStorageKey);
      if (!raw) return { startTime: 0, endTime: 0, watchedSeconds: 0 };
      const parsed = JSON.parse(raw);
      return parsed[mediaId] || { startTime: 0, endTime: 0, watchedSeconds: 0 };
    } catch (e) {
      console.error("Error reading from localStorage", e);
      return { startTime: 0, endTime: 0, watchedSeconds: 0 };
    }
  };

  const clearProgressFromLocalStorage = () => {
    try {
      const raw = localStorage.getItem(localStorageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      delete parsed[mediaId];
      localStorage.setItem(localStorageKey, JSON.stringify(parsed));
    } catch (e) {
      console.error("Error clearing from localStorage", e);
    }
  };
  const callActivityLogAPI = useCallback(async () => {
    if (selectedMedia) {
      if (selectedMedia.isViewed) return;
      try {
        await saveSessionActivityMutation
          .mutateAsync({
            input: {
              activityType: SessionMediaReviewActivityLogEnum.TimeSpent,
              duration: watchedSecondsRef.current,
              startTime: playStartedTimeRef.current ?? 0,
              endTime: currentTimeRef.current,
              mediaId: mediaId,
              id: mediaActivityLogIdRef.current || undefined,
            },
          })
          .then((data) => {
            if (data.createUpdateSessionMediaReviewActivityLog) {
              const newId = data?.createUpdateSessionMediaReviewActivityLog?.mediaActivityLog?.id;
              if (newId) mediaActivityLogIdRef.current = newId;
              saveProgressToLocalStorage();
            }
          });
      } catch (e) {
        console.error("Failed to log activity", e);
      }
    }
  }, [selectedMedia, mediaId]);

  // Keep the ref updated with the latest function
  useEffect(() => {
    callActivityLogAPIRef.current = callActivityLogAPI;
  }, [callActivityLogAPI]);

  const startTimer = () => {
    intervalRef.current = setInterval(() => {
      watchedSecondsRef.current += 1;
      tickRef.current += 1;
      if (tickRef.current % 10 === 0) {
        callActivityLogAPIRef.current();
      }
    }, 1000);
  };

  const stopTimer = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    tickRef.current = 0;
  };

  const handlePlay = () => {
    if (isSessionTracking) {
      if (playStartedTimeRef.current === null) {
        playStartedTimeRef.current = currentTimeRef.current;
      }

      startTimer();
    }
  };

  const handlePause = async () => {
    if (isSessionTracking) {
      stopTimer();
      await callActivityLogAPIRef.current();

      // Clear activity log tracking
      mediaActivityLogIdRef.current = null;
      playStartedTimeRef.current = currentTimeRef.current;
      watchedSecondsRef.current = watchedSecondsRef.current < 10 ? watchedSecondsRef.current : 0;

      // Clear persisted ID
      const raw = localStorage.getItem(localStorageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed[mediaId]) {
          delete parsed[mediaId].activityLogId;
          localStorage.setItem(localStorageKey, JSON.stringify(parsed));
        }
      }
    }
  };

  const handleEnded = () => {
    if (isSessionTracking) {
      stopTimer();
      callActivityLogAPIRef.current();
      mediaActivityLogIdRef.current = null;
      playStartedTimeRef.current = null;
      watchedSecondsRef.current = 0;
      currentTimeRef.current = 0;
      clearProgressFromLocalStorage();
    }
  };
  useEffect(() => {
    if (!isSessionTracking && !isFetching && selectedMedia && selectedMedia.isViewed) return;

    if (selectedMedia && !selectedMedia.isViewed) {
      // Load saved progress when component mounts or mediaId changes
      const saved = getSavedProgress();

      mediaActivityLogIdRef.current = saved.activityLogId || null;
      watchedSecondsRef.current = saved.watchedSeconds;
      currentTimeRef.current = saved.endTime;
      playStartedTimeRef.current = saved.startTime;

      setTimeout(() => {
        if (playerRef.current && saved.endTime > 0) {
          playerRef.current.seekTo(saved.endTime);
        }
      }, 500);
    }
    // Define cleanup function for unload or hidden state
    const handleExit = () => {
      if (
        isSessionTracking &&
        watchedSecondsRef.current > 0 &&
        !isFetching &&
        selectedMedia &&
        !selectedMedia.isViewed
      ) {
        stopTimer();
        callActivityLogAPIRef.current();
      }
    };
    // Add listeners
    const onBeforeUnload = () => {
      if (!isFetching && selectedMedia && !selectedMedia.isViewed) {
        handleExit();
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden" && !isFetching && selectedMedia && !selectedMedia.isViewed) {
        handleExit();
      }
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    document.addEventListener("visibilitychange", onVisibilityChange);

    // Cleanup
    return () => {
      if (!isFetching && selectedMedia && !selectedMedia.isViewed) {
        handleExit();
      } // also called on component unmount
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [isSessionTracking, selectedMedia]);

  const MemoizedVideoPlayerContent = useMemo(
    () => (
      <VideoPlayerContent
        enabledFocus={enabledFocus}
        ref={playerRef}
        key={mediaId}
        videoUrl={videoUrl}
        onDurationChange={handleDurationChange}
        onSeeked={onSeeked}
        onTimeUpdate={(time) => {
          setCurrentTime(time);
          currentTimeRef.current = time;
        }}
        onSeeking={onSeeking}
        onPlayerReady={onPlayerReady}
        onPlay={handlePlay}
        onPause={handlePause}
        onEnd={handleEnded}
      />
    ),
    [videoUrl, mediaId],
  );

  return (
    <div className="w-full aspect-video" data-video-player>
      {MemoizedVideoPlayerContent}
      {selectedMedia ? (
        <div className="relative flex flex-col gap-2 lg:p-0 sm:p-6 p-4">
          <EmojiCommentButtons
            onEmojiClick={handleEmojiClick}
            disabled={currentDuration <= 0}
            selectedEmoji={selectedEmoji}
            onCommentSubmit={handleCommentSubmit}
            onClose={closeCommentBox}
            getCurrentTime={getCurrentTime}
            playerRef={playerRef}
            commentCount={commentCount}
            media={selectedMedia}
            isCommentSaving={isCommentSaving}
          />

          {/* With the Maladaptive Feature Flag - The Emoji Comment Panel (now called the CommentsTimeline) 
        is below the buttons, alongside the new Moments Timel*/}

          <CommentsTimeline
            emojiMarkers={emojiMarkers}
            isLoading={isLoadingComments || currentDuration <= 0}
            mediaId={mediaId || ""}
            onMarkerClick={handleMarkerClick}
            onTimelineClick={onTimelineClick}
            duration={currentDuration}
            currentTime={currentTime}
          />

          <MomentsTimeline
            media={selectedMedia}
            onTimelineClick={onTimelineClick}
            onMarkerClick={handleMomentMarkerClick}
            duration={currentDuration}
            currentTime={currentTime}
            isLoading={currentDuration <= 0}
          />
        </div>
      ) : null}
    </div>
  );
};
